import { Component, ElementRef, Input, OnInit, ViewChild, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { HelperService } from '@app/core/services/helper.service';
import { HasRequiredField } from '@app/ui-forms/classes/has-required-field';

@Component({
	selector: 'ui-text-control',
	templateUrl: './text-control.component.html',
	styleUrls: ['../../styles/form-controls.less', './text-control.component.less'],
})
export class TextControlComponent implements OnInit, OnChanges {
	@Input() form: FormGroup;
	@Input() form_control: FormControl;
	@Input() type: string = null;
	@Input() label: string = null;
	@Input() render_label_placeholder = false;
	@Input() placeholder: string = null;
	@Input() info: string = null;
	@Input() input_css_class: string = '';
	@Input() highlighted_info: boolean = false;
	@Input() prefix: string = null;
	@Input() suffix: string = null;
	@Input() spellcheck: boolean = false;
	@Input() max_length: number = 500000;
	@Input() autosize: boolean = true;
	@Input() show_error_text: boolean = true;
	@Input() autocomplete: boolean = true;
	@Input() min_date: string = null;
	@Input() max_date: string = null;
	@Input() show_validation_icon = false;

	@Output() text_input: EventEmitter<any> = new EventEmitter();

	@ViewChild('textarea') textarea: ElementRef;

	form_control_id: string = '';
	form_control_name: string = '';
	input_type: string = null;
	input_css_classes: string = '';

	fake_password: boolean = false;
	is_focused: boolean = false;

	constructor(private helper_service: HelperService) {}

	ngOnInit(): void {
		let form_control_name = null;
		const parent = this.form_control.parent;

		if (parent instanceof FormGroup) {
			Object.keys(parent.controls).forEach((name) => {
				if (this.form_control === parent.controls[name]) {
					form_control_name = name;
				}
			});
		}

		this.form_control_name = form_control_name;
		this.form_control_id = 'textbox_' + this.helper_service.generateRandomString(30);

		switch (this.type) {
			case 'currency':
				this.input_type = 'number';
				break;
			case 'fake-password':
				this.input_type = 'number';
				this.fake_password = true;
				break;
			default:
				this.input_type = this.type;
				break;
		}

		this.input_css_classes = this.getInputCSSClass();
	}

	ngOnChanges(simple_changes: SimpleChanges): void {
		if (simple_changes['input_css_class']) {
			this.input_css_classes = this.getInputCSSClass();
		}
	}

	isNotEmptyOrActive(): boolean {
		return (this.form_control.value !== null && this.form_control.value !== '') || this.isInvalid();
	}

	get fakePasswordClasses(): string {
		if (!this.fake_password) return this.input_css_classes;
		const classes = !this.form_control.value ? this.input_css_classes : `secured-text`;
		return classes;
	}

	get isSpecialInput(): boolean {
		const special_types = ['textarea', 'number'];
		return special_types.includes(this.input_type);
	}

	isInvalid(): boolean {
		return (this.form_control.dirty || this.form_control.touched) && this.form_control.invalid;
	}

	/**
	 * Checks if control passes validation.
	 *
	 * @returns {boolean}
	 */
	isValid(): boolean {
		return this.form_control.valid && this.form_control.value;
	}

	isRequired(): boolean {
		return HasRequiredField(this.form_control);
	}

	getInputCSSClass(): string {
		const classes = [];

		classes.push(this.input_css_class);

		if (this.is_focused) {
			classes.push('in-focus');
		}

		if (this.type === 'textarea' && !this.autosize) {
			classes.push('set-size');
		}

		if (this.type === 'date' && !this.form_control.value) {
			classes.push('has-placeholder');
		}

		return classes.join(' ');
	}

	getError(): string {
		if (this.isInvalid() && this.show_error_text) {
			if (this.form_control.hasError('required')) {
				return 'is required';
			}

			if (this.form_control.hasError('isBlank')) {
				return 'is blank';
			}

			return 'is invalid';
		}
	}

	getRequired(): string {
		if (!this.getError() && this.show_error_text) {
			return this.isRequired() ? ' *' : '';
		}
	}

	onFocus(): void {
		this.is_focused = true;
		if (this.fake_password) {
			this.input_type = 'number';
		}
		this.input_css_classes = this.getInputCSSClass();
	}

	onBlur(): void {
		this.is_focused = false;
		this.input_css_classes = this.getInputCSSClass();
	}

	onInput(): void {
		this.text_input.emit();
		this.input_css_classes = this.getInputCSSClass();
	}
}
